/* READING THE SERVER'S SCOPE DECISION — pure, zero imports.
 *
 * ⚠️ THIS FILE DECIDES NOTHING. `/api/tam` is scope-enforced by
 * `resolveScope()` on the backend, and the whole point of this module is to
 * READ that answer rather than form a second opinion about it. A client that
 * re-derived "can this person see that book" would be a second copy of an
 * authorization rule, and the two would disagree the first time either changed
 * — the drift fault this codebase keeps finding, except on a permission.
 *
 * Hard rule 5 says it plainly: `can()` decides what gets DRAWN, the server
 * decides what is allowed. So: the switcher's options come from the server's
 * own rep list, and a refusal is rendered in the server's own words.
 */

/** The three refusals `resolveScope()` can return, and what each one means. */
export const DENIAL = {
  /* Your sign-in is not mapped to a rep value on the records. An EXPECTED
   * state, not an edge case: `sr_assigned_rep` is a hand-entered display name
   * and most reps are not HubSpot owners at all, so the mapping is the
   * exception rather than the rule. (The server's own comment carries the
   * measured proportion; it is not restated here undated.) */
  rep_unmapped: { key: 'rep_unmapped', tone: 'warn', fixedBy: 'an executive' },
  /* A rep asked for someone else while the toggle is off. A SETTING, not an
   * empty result. */
  team_visibility_off: { key: 'team_visibility_off', tone: 'info', fixedBy: 'an executive' },
  /* The name asked for is not a rep on the records. */
  rep_unknown: { key: 'rep_unknown', tone: 'warn', fixedBy: null },
}

const S = (v) => String(v == null ? '' : v).trim()

/**
 * Read a `/api/tam` response (or a 403 body) into what the UI needs.
 *
 * ⚠️ A DENIAL IS NOT AN EMPTY RESULT, and the distinction is the entire reason
 * these routes answer `render: "error-not-empty"`. The server writes a sentence
 * for the person reading it — *"It is not showing you an empty book, it cannot
 * identify one"* — and `reason` is rendered VERBATIM. Paraphrasing it would
 * lose the one thing it is for, and showing a zero instead would be the
 * four-false-zeros bug reborn.
 */
export function readTeamScope(payload) {
  if (!payload || typeof payload !== 'object') {
    return {
      allowed: false, scope: 'denied', rep: null,
      denial: null,
      reason: 'The team view did not load, so there is nothing to show yet — not a book of zero.',
      reps: [], team: null, pool: null,
      visibility: { enabled: null, degraded: null, detail: null },
      verification: null,
    }
  }

  const allowed = payload.ok === true && payload.scope && payload.scope !== 'denied'
  const code = S(payload.error) || S(payload.code) || null

  return {
    allowed: Boolean(allowed),
    scope: S(payload.scope) || (allowed ? 'own' : 'denied'),
    rep: S(payload.rep) || S(payload.renderedAs) || null,
    denial: code ? (DENIAL[code] || { key: code, tone: 'warn', fixedBy: null }) : null,
    /* The server's sentence, not mine. `detail` is what these routes use for a
     * refusal; `reason` is what resolveScope returns. Either is verbatim. */
    reason: S(payload.detail) || S(payload.reason) || null,

    /* The switcher's options come from the SERVER's rep list, never a client
     * roster. A hand-kept list would drift the first time somebody is hired,
     * and worse, it would offer names the server will refuse. */
    reps: Array.isArray(payload.team?.rows)
      ? payload.team.rows.map((r) => S(r.rep)).filter(Boolean)
      : Array.isArray(payload.team)
        ? payload.team.map((r) => S(r.rep)).filter(Boolean)
        : [],

    team: payload.team || null,
    pool: payload.pool || null,

    visibility: {
      enabled: payload.visibility?.enabled ?? null,
      /* ⚠️ `degraded` MEANS THE SETTING COULD NOT BE READ, which is not the same
       * as the setting being off. Collapsing the two would tell a rep team
       * visibility is disabled when in truth nobody knows. */
      degraded: payload.visibility?.degraded ?? null,
      detail: S(payload.visibility?.detail) || null,
      changedBy: S(payload.visibility?.changedBy) || null,
      at: S(payload.visibility?.at) || null,
    },

    /* ⚠️ A VERIFICATION RENDER IS STAMPED AND MUST SAY SO ON SCREEN. The server
     * marks a read-only verification identity so a screenshot can never be
     * mistaken for what a real rep saw. Dropping that stamp in the UI would
     * defeat the reason it is on the response. */
    verification: payload.verificationRender
      ? {
        renderedAs: S(payload.renderedAs) || null,
        renderedBy: S(payload.renderedBy) || null,
        note: S(payload.renderNote) || null,
      }
      : null,
  }
}

/**
 * Who this session may stand in, as OPTIONS for the switcher.
 *
 * ⚠️ DRAWING ONLY. The server refuses a name it does not allow, so this is
 * about not offering a door that will be slammed — it is not the lock.
 */
/**
 * @param counts optional `{ [rep]: n }` counted from the championship book the
 *   page has ALREADY loaded. When present it decides `hasBook`, because it is
 *   the only source here that is known to be complete.
 */
export function switcherOptions(scope, { ownRep = '', roster = [], counts = null } = {}) {
  const own = S(ownRep)
  const out = []
  const seen = new Set()
  /* 🔴 `hasBook` IS THREE-VALUED, AND THAT IS NOT PEDANTRY.
   *
   * The server's `/api/tam?rep=*` rollup is INCOMPLETE — measured 2026-10-05,
   * it returned 10 rows and omitted Jake Highter (1,007 records) and Abigail
   * Mancuso (339). So "absent from that list" does NOT mean "holds nothing",
   * and the first cut of this function treated it as though it did: Jake would
   * have rendered "— no records yet" while holding a thousand records. That is
   * a worse lie than leaving him off, because it is specific and confident.
   *
   * `true` holds records · `false` holds none · `null` NOBODY KNOWS. */
  const known = counts && typeof counts === 'object'
  const lookup = known
    ? new Map(Object.entries(counts).map(([k, v]) => [k.toLowerCase(), Number(v) || 0]))
    : null

  const push = (value, label, kind, fallback) => {
    const k = value.toLowerCase()
    if (seen.has(k)) return
    seen.add(k)
    /* The counted book wins whenever we have it. */
    const hasBook = lookup ? (lookup.get(k) || 0) > 0 : fallback
    out.push({ value, label, kind, hasBook, records: lookup ? (lookup.get(k) || 0) : null })
  }
  if (own) push(own, `${own} — you`, 'own', true)

  /* `scope: 'all'` is what the server returns to an exec, and to a rep when the
   * toggle is on. Either way it is the server saying so, not us inferring it. */
  if (scope?.scope === 'all') {
    push('*', 'The whole team', 'all', true)
    for (const member of scope.coverageMembers || []) push(`school-team:${member.id}`,`${member.name} — bowl coverage (${member.role})`,'coverage',null)

    /* Who HOLDS records, from the server. These are known-good names. */
    const held = new Set()
    for (const r of scope.reps || []) {
      held.add(r.toLowerCase())
      if (own && r.toLowerCase() === own.toLowerCase()) continue
      push(r, r, 'other', true)
    }

    /* 🔴 AND WHO MAY BE GIVEN RECORDS, WHICH IS A DIFFERENT AND LONGER LIST.
     *
     * Brian: "I noticed we aren't showing Jake or Eric Graham. Also John
     * Haggerty with letterwinner opportunities."
     *
     * ⚠️ THE SWITCHER WAS BUILT FROM `team.rows` — the TAM rollup — which
     * contains only people who ALREADY HOLD records. So a rep with an empty
     * book was not merely shown as empty, they were UNSELECTABLE, and the
     * first person who needed to be given work was the one person who could
     * not be looked at. A person-switcher must list the TEAM.
     *
     * ⚠️ AND `assignable` IS THE TEST, NEVER THE CATEGORY. John Haggerty is
     * Leadership/Executive and also leads the letterwinner lane as a working
     * owner. Filtering reps by job title would hide exactly the person this
     * complaint was about. */
    for (const m of roster) {
      const name = S(m?.name ?? m)
      if (!name) continue
      if (own && name.toLowerCase() === own.toLowerCase()) continue
      /* ⚠️ `null`, NOT `false`, WHEN THE ROLLUP IS THE ONLY SOURCE. Absent from
       * an incomplete list is not evidence of an empty book — see above. */
      push(name, name, 'other', held.has(name.toLowerCase()) ? true : null)
    }
  }
  return out
}

/**
 * ⚠️ A REP WITH NO RECORDS IS AN EMPTY BOOK, NOT AN ERROR.
 *
 * The server answers `rep_unknown` for a name that appears on no record — which
 * is literally true and reads as a fault. For somebody who has simply not been
 * given anything yet it is the normal, expected, actionable state, and the
 * sentence a rep needs is "nothing is assigned", not "unknown rep".
 *
 * This is only applied to a name the ROSTER vouches for. A genuinely unknown
 * name still reads as unknown — softening that would hide a typo.
 */
export function emptyBookReason(scope, { name = '', onRoster = false } = {}) {
  if (!onRoster) return null
  if (scope?.denial?.key !== 'rep_unknown') return null
  return `${S(name) || 'This person'} is on the team but holds no championships yet. `
    + 'That is an empty book waiting to be filled, not a missing one — assign records from '
    + 'the Command Center and they will appear here.'
}

/**
 * ⚠️ MONEY IS EXEC-ONLY REGARDLESS OF THE TOGGLE. Seeing a colleague's workload
 * is not seeing their compensation, and the team-visibility switch was never
 * about money. This is a DRAWING decision mirroring the server's; the server
 * is what must refuse the figures.
 */
export const maySeeTeamMoney = (isExec) => isExec === true
